import { FormControl, FormHelperText, FormLabel } from '@mui/material';
import { Resource } from 'json-api-hooks';
import React from 'react';
import { Controller, ControllerFieldState, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form';

import { saveToStorage } from '../../lib/storage';
import Markdown from '../Markdown';

import useFormFieldCondition from './lib/useFormFieldCondition';
import MultipleFields from './MultipleFields';
import OmniInput from './OmniInput';
import { FormFieldDefinition } from './ResourceForm';

const isCheckBoxInput = (element: any) => element.type === 'checkbox';
const isDateObject = (value: unknown) => value instanceof Date;
const isNullOrUndefined = (value: unknown) => value == null;
const isObjectType = (value: unknown) => typeof value === 'object';
const isObject = (value: unknown) =>
  !isNullOrUndefined(value) && !Array.isArray(value) && isObjectType(value) && !isDateObject(value);
const getEventValue = (event: any) =>
  isObject(event) && event.target ? (isCheckBoxInput(event.target) ? event.target.checked : event.target.value) : event;

interface RenderedFieldProps {
  field: ControllerRenderProps<FieldValues, string>;
  fieldState: ControllerFieldState;
  formField: FormFieldDefinition;
  resource: Resource;
  storageKey?: string;
}

const hiddenStyle = {
  height: '1px',
  left: '-10000px',
  overflow: 'hidden',
  position: 'absolute',
  top: 'auto',
  width: '1px',
};

const RenderedField = ({ field, fieldState, formField, resource, storageKey }: RenderedFieldProps) => {
  const onChange = (e: any) => {
    if (storageKey) {
      saveToStorage(storageKey, JSON.stringify(getEventValue(e)));
    }
    field.onChange(e);
  };
  const helper = fieldState.error?.message ?? formField.helper;

  return (
    <FormControl
      error={!!fieldState.error}
      required={formField.required}
      sx={formField.type === 'hidden' ? hiddenStyle : undefined}
    >
      <FormLabel id={`${field.name}-label`}>{formField.label}</FormLabel>
      <Markdown noSpacing text={formField.description} />
      {formField.multiple ? (
        <MultipleFields
          fieldState={fieldState}
          inputProps={{ ...field, onChange }}
          resource={resource}
          storageKey={storageKey}
          {...formField}
        />
      ) : (
        <OmniInput
          fieldState={fieldState}
          inputProps={{ ...field, onChange }}
          resource={resource}
          storageKey={storageKey}
          {...formField}
        />
      )}
      {helper && (
        <FormHelperText id={`${field.name}-helper`} sx={{ marginLeft: 0, fontSize: '.8em', color: 'grey.main' }}>
          {helper}
        </FormHelperText>
      )}
    </FormControl>
  );
};

interface FormFieldProps {
  defaultValue?: unknown;
  formField: FormFieldDefinition;
  prefix?: string;
  resource?: Resource;
  storageKeyPrefix?: string;
}

const FormField = ({ defaultValue, formField, prefix, resource, storageKeyPrefix }: FormFieldProps) => {
  const { control } = useFormContext();
  const inputName = prefix ? `${prefix}.${formField.attribute}` : formField.attribute;
  const showField = useFormFieldCondition(resource, formField);
  const storageKey = storageKeyPrefix ? `${storageKeyPrefix}-${formField.attribute}` : undefined;

  if (!showField) {
    return null;
  }

  return (
    <Controller
      control={control}
      defaultValue={defaultValue ?? ''}
      name={inputName}
      render={({ field, fieldState }) => (
        <RenderedField
          field={field}
          fieldState={fieldState}
          formField={formField}
          resource={resource!}
          storageKey={storageKey}
        />
      )}
      rules={{ required: formField.required }}
    />
  );
};

export default FormField;
